import React, { Fragment, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, AlertCircle, Loader, Ban, Shield } from 'lucide-react';
type EscrowStatus = 'pending' | 'funded' | 'held' | 'released' | 'refunded' | 'disputed';
interface StatusStep {
  status: EscrowStatus;
  label: string;
  date?: string;
}
interface EscrowStatusTrackerProps {
  currentStatus: EscrowStatus;
  steps: StatusStep[];
}
const STATUS_CONFIG: Record<EscrowStatus, {
  icon: React.ElementType;
  color: string;
  bgColor: string;
}> = {
  pending: {
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
  },
  funded: {
    icon: Shield,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  held: {
    icon: AlertCircle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30'
  },
  released: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  refunded: {
    icon: Ban,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 dark:bg-gray-700'
  },
  disputed: {
    icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  }
};
const STATUS_ORDER: EscrowStatus[] = ['pending', 'funded', 'held', 'released'];
export function EscrowStatusTracker({
  currentStatus,
  steps
}: EscrowStatusTrackerProps) {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  const isTerminal = currentStatus === 'refunded' || currentStatus === 'disputed';
  const getStepStatus = (stepStatus: EscrowStatus, index: number) => {
    if (isTerminal && stepStatus === currentStatus) return 'current';
    const stepIndex = STATUS_ORDER.indexOf(stepStatus);
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="relative" data-id="element-1105">
      <div className="flex items-center justify-between" data-id="element-1106">
        {STATUS_ORDER.map((status, index) => {
        const step = steps.find(s => s.status === status);
        const stepStatus = getStepStatus(status, index);
        const config = STATUS_CONFIG[status];
        const Icon = config.icon;
        return <Fragment key={status} data-id="element-1107">
              {/* Step */}
              <div className="flex flex-col items-center relative z-10" data-id="element-1108">
                <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: index * 0.1
            }} className={`w-12 h-12 rounded-full flex items-center justify-center ${stepStatus === 'completed' ? 'bg-green-500' : stepStatus === 'current' ? config.bgColor : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-1109">
                  {stepStatus === 'completed' ? <CheckCircle className="h-6 w-6 text-white" data-id="element-1110" /> : stepStatus === 'current' ? currentStatus === status ? <Loader className={`h-6 w-6 ${config.color} animate-spin`} data-id="element-1111" /> : <Icon className={`h-6 w-6 ${config.color}`} data-id="element-1112" /> : <Icon className="h-6 w-6 text-gray-400 dark:text-gray-500" data-id="element-1113" />}
                </motion.div>
                <p className={`mt-2 text-xs font-medium capitalize ${stepStatus === 'pending' ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`} data-id="element-1114">
                  {status}
                </p>
                {step?.date && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1115">
                    {formatDate(step.date)}
                  </p>}
              </div>

              {/* Connector Line */}
              {index < STATUS_ORDER.length - 1 && <div className="flex-1 h-1 mx-2 relative" data-id="element-1116">
                  <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded" data-id="element-1117" />
                  <motion.div initial={{
              width: 0
            }} animate={{
              width: stepStatus === 'completed' ? '100%' : '0%'
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="absolute inset-y-0 left-0 bg-green-500 rounded" data-id="element-1118" />
                </div>}
            </Fragment>;
      })}
      </div>

      {/* Terminal Status Indicator */}
      {isTerminal && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`mt-4 p-3 rounded-lg ${STATUS_CONFIG[currentStatus].bgColor} border ${currentStatus === 'disputed' ? 'border-red-200 dark:border-red-800' : 'border-gray-200 dark:border-gray-600'}`} data-id="element-1119">
          <div className="flex items-center space-x-2" data-id="element-1120">
            {createElement(STATUS_CONFIG[currentStatus].icon, {
          className: `h-5 w-5 ${STATUS_CONFIG[currentStatus].color}`
        })}
            <span className={`text-sm font-medium ${STATUS_CONFIG[currentStatus].color}`} data-id="element-1121">
              Escrow{' '}
              {currentStatus === 'refunded' ? 'Refunded' : 'Under Dispute'}
            </span>
          </div>
        </motion.div>}
    </div>;
}